import React, { useState } from "react";
import {
  Card,
  Grid,
  Typography,
  Divider,
  Paper,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import CChooseFile from "../../../basecomp/components/CChooseFile";
import CButton from "../../../basecomp/components/CButton";
import CBackDrop from "../../../basecomp/components/CBackDrop";
import CDropDown from "../../../basecomp/components/CDropDown";
import GLNUploadVM from "../viewmodel/GLNUploadVM";

const GLNUploadView = () => {
  const {
    selectedFile,
    isLoading,
    uploadType,
    selectedCountry,
    handleFileChoose,
    handleUploadFile,
    hasBtnPermission,
    handleSampleFileDownload,
    handleBicSmdgDownload,
    handleGlnTableDownload,
    handleUploadTypeChange,
    handleCountryChange,
  } = GLNUploadVM();

  const [activeTab, setActiveTab] = useState(0);
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (!hasBtnPermission()) {
    return null;
  }

  return (
    <>
      <CBackDrop cOpen={isLoading} />
      <Grid
        container
        spacing={3}
        sx={{ margin: "6em 0 3em 0", padding: "0 2em" }}
      >
        <Grid item xs={12}>
          <Paper sx={{ borderRadius: 2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
              aria-label="GLN operations tabs"
            >
              <Tab
                label="Upload Files"
                icon={<CloudUploadIcon />}
                iconPosition="start"
              />
              <Tab
                label="Import Data"
                icon={<FileDownloadIcon />}
                iconPosition="start"
              />
            </Tabs>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          {activeTab === 0 ? (
            <Card sx={{ padding: "2em", borderRadius: 2 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 600, mb: 3 }}
              >
                Upload Files
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <CDropDown
                    cId="uploadTypeDropdown"
                    cHelperText="Upload Type"
                    cData={["SUBREGION", "UNLOCODE", "IMO"]}
                    cOnChange={handleUploadTypeChange}
                    cFullWidth={true}
                    cDefaultValue={uploadType}
                    cIsRequired={true}
                  />
                </Grid>

                <Grid item xs={12} md={8}>
                  <CChooseFile
                    cFileType="XCL"
                    cTooltipText={`Upload ${uploadType.toUpperCase()} file`}
                    cHelperText="*File Types Excel/CSV"
                    cHandleFileChange={handleFileChoose}
                    cFullWidth={true}
                    cIsRequired={true}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" mt={2}>
                    <CButton
                      cText={`Download Sample ${uploadType.toUpperCase()} File`}
                      cTooltipText={`Download the sample ${uploadType.toUpperCase()} file`}
                      cOnClick={handleSampleFileDownload}
                      cStartIcon={<FileDownloadIcon />}
                      cVariant="outlined"
                    />

                    <CButton
                      cId="glnUploadBtn"
                      cText="Upload File"
                      cTooltipText={`Upload and process ${uploadType.toUpperCase()} file`}
                      cOnClick={handleUploadFile}
                      cDisabled={!selectedFile}
                      cStartIcon={<CloudUploadIcon />}
                      cColor="primary"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Card>
          ) : (
            <Card sx={{ padding: "2em", borderRadius: 2 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 600, mb: 3 }}
              >
                Import Data
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 3, height: "100%" }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={500}
                      gutterBottom
                    >
                      Import Facility Codes
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      Download BIC and SMDG for facility table
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                      <CDropDown
                        cId="facilitiesCountryDropdown"
                        cHelperText="Select Country"
                        cData={["All", "NL", "IN"]} // Add more countries as needed
                        cOnChange={handleCountryChange}
                        cFullWidth={true}
                        cDefaultValue={selectedCountry}
                      />

                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <CButton
                          cText="Import Facility Codes"
                          cTooltipText="Download BIC and SMDG facility codes"
                          cOnClick={handleBicSmdgDownload}
                          cStartIcon={<FileDownloadIcon />}
                          cColor="primary"
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 3, height: "100%" }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={500}
                      gutterBottom
                    >
                      Import GLN Table
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      Download BIC and SMDG for gln table
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                      <CDropDown
                        cId="glnTableCountryDropdown"
                        cHelperText="Select Country"
                        cData={["All", "NL", "IN"]} // Add more countries as needed
                        cOnChange={handleCountryChange}
                        cFullWidth={true}
                        cDefaultValue={selectedCountry}
                      />

                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <CButton
                          cText="Import GLN Table"
                          cTooltipText="Download complete GLN table data as CSV"
                          cOnClick={handleGlnTableDownload}
                          cStartIcon={<BackupTableIcon />}
                          cColor="primary"
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Card>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default GLNUploadView;
